// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import styles from './HistoryCollectComplete.module.scss';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createAxios } from 'ts/createAxios';
import {
  ByTenant,
  CollectHistory,
  CollectHistoryListResponse,
} from 'util/Types';
import {
  getFormatCurrentDate,
  getFormatCurrentDateTime,
  getFormatDateWithCurrentTimezone,
} from 'ts/formatDate';
import { useTranslation } from 'react-i18next';
import { DATE_FORMAT, DEFAULT_PAGE } from 'util/ConstantValues';
import { getCollectTenantMapper, normalizeNumber } from 'util/commons';
import { IconDownload } from 'components/atoms/icons/IconDownload';
import { getCollectHistoryCSVDataGroupedByTenant } from 'util/csvExportUtils';
import { handleError } from 'util/errorHandler';
import { getCollectDeliveries } from 'apis/collects';
import CustomPagination from 'components/atoms/CustomPagination';
import CollectDeliveriesDataModal from 'components/molecules/CollectDeliveriesDataModal';
import ResponsiveHistoryTable from 'components/molecules/ResponsiveHistoryTable';
import { Column } from 'react-table';
import CustomCSVLink from 'components/atoms/CustomCsvLink';
import dayjs from 'dayjs';
import IconCalendarTablet from 'components/atoms/icons/IconCalendarTablet';
import TableFilterCalendar from 'components/molecules/TableFilterCalendar';

export type Props = {
  id?: string;
};

export const HistoryCollectComplete = () => {
  const { t } = useTranslation();
  const [collectedData, setCollectedData] = useState<CollectHistory[]>([]);
  const [pageMeta, setPageMeta] = useState<{
    page: number;
    total: number;
  }>({
    page: 1,
    total: 0,
  });
  const [emissionDetailCollectId, setEmissionDetailCollectId] =
    useState<number>();
  const [exportCsvCollectData, setExportCsvCollectData] = useState<{
    id: number;
    targetIds: string[];
    byTenant: ByTenant;
    wasteUnitName: string | undefined;
  }>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const csvRef = useRef<any>(null);
  const [dateRange, setDateRange] = useState<[string, string]>();

  const pageCount = Math.ceil(pageMeta.total / 50);

  const callApi = async (page = pageMeta.page) => {
    try {
      const res = await createAxios({
        prefix: 'v2',
      }).get<CollectHistoryListResponse>('/work-histories/collect/delivered', {
        params: {
          page,
          take: 50,
          ...(dateRange && {
            dateFrom: dayjs(dateRange[0]).format(DATE_FORMAT.full_date),
            dateTo: dayjs(dateRange[1]).format(DATE_FORMAT.full_date),
          }),
        },
      });

      setCollectedData(res.data.deliveries);
      setPageMeta({
        page: res.data.page,
        total: res.data.total,
      });
    } catch (e) {
      handleError(e);
    }
  };

  const handlePageClick = (selectedItem: { selected: number }) => {
    callApi(selectedItem.selected + 1);
  };

  const handleExportCSV = async (collect: CollectHistory) => {
    await getCollectDeliveries({
      deliveryIds: [collect.id],
      cursor: null,
      successCallback: (data) => {
        setExportCsvCollectData({
          id: collect.id,
          targetIds: data
            .map((item) => item.processingObjectId)
            .filter(Boolean),
          byTenant: getCollectTenantMapper(data)[collect.id] || {},
          wasteUnitName: data.find(
            ({ deliveryId }) => deliveryId.toString() === collect.id.toString()
          )?.wasteUnitName,
        });

        setTimeout(() => {
          csvRef.current?.link?.click();
          setExportCsvCollectData(undefined);
        }, 100);
      },
      failedCallback: () => {
        setExportCsvCollectData(undefined);
      },
    });
  };

  useEffect(() => {
    callApi(DEFAULT_PAGE);
  }, [dateRange]);

  const columns: Column<CollectHistory>[] = useMemo(
    () => [
      {
        Header: t('common_history_field.date_time') as string,
        accessor: (row) => row.deliveredDatetime,
        Cell: ({ value }) => (
          <>
            {value &&
              getFormatDateWithCurrentTimezone(value, DATE_FORMAT.slaYMDHm)}
          </>
        ),
      },
      {
        Header: t('common_history_field.responsible_person') as string,
        accessor: (row) => row.userId + ' (' + row.userName + ')',
        Cell: ({ value }) => <>{value}</>,
      },
      {
        Header: t('common_history_field.place') as string,
        accessor: (row) => row.areaName,
        Cell: ({ value }) => <>{value}</>,
      },
      {
        Header: t('common_history_field.material_name') as string,
        accessor: (row) => row.description,
        Cell: ({ value }) => <>{value}</>,
      },
      {
        Header: t('common_history_field.item') as string,
        accessor: (row) => row.wasteUnitName || '-',
        Cell: ({ value }) => <>{value}</>,
      },
      {
        Header: t('common_history_field.emission_registered_weight') as string,
        accessor: (row) => row.weight,
        Cell: ({ value }) => <>{normalizeNumber({ value }) + 'kg'}</>,
      },
      {
        Header: '',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        accessor: 'actions' as any,
        Cell: ({ row }) => (
          <div className={styles.actionCell}>
            <button
              onClick={() => setEmissionDetailCollectId(row.original.id)}
              className={styles.viewButton}
            >
              {t('common.button.view_details')}
            </button>

            <div
              className={styles.download}
              onClick={() => handleExportCSV(row.original)}
            >
              <IconDownload strokeColor="black" />
            </div>

            {exportCsvCollectData != null &&
              exportCsvCollectData?.id === row.original.id && (
                <CustomCSVLink
                  className={styles.csvLink}
                  ref={csvRef}
                  filename={`${t(
                    'histories.csv_file_name_collect_by_waste_unit'
                  )}_${getFormatCurrentDate()}`}
                  data={getCollectHistoryCSVDataGroupedByTenant({
                    type: 'delivered',
                    data: row.original,
                    targetIds: exportCsvCollectData.targetIds,
                    byTenant: exportCsvCollectData.byTenant,
                    exportDatetimeString: getFormatCurrentDateTime(),
                    wasteUnitName: exportCsvCollectData.wasteUnitName,
                    t,
                  })}
                />
              )}
          </div>
        ),
      },
    ],
    [t, exportCsvCollectData]
  );

  return (
    <div className={styles.historyCollectCompletes}>
      <div className={styles.historyCollectedHeader}>
        <TableFilterCalendar
          wrapperClassName={styles.dateFilter}
          expanded={{
            placeholder: t('common.placeholder.select_date_range'),
            icon: <IconCalendarTablet />,
          }}
          portalPositionPoint={(ref) => ref}
          endDateRequired
          value={dateRange}
          onChange={setDateRange}
        />
      </div>

      <div className={styles.historyCollectedContent}>
        <ResponsiveHistoryTable<CollectHistory>
          data={collectedData}
          columns={columns}
          noDataMessage={t('messages.M_008') as string}
          scrollResetDependencies={[pageMeta]}
          className={styles.responsiveTable}
        />
      </div>

      {pageMeta.total > 0 && (
        <div className={styles.paginationContainer}>
          <CustomPagination
            pageCount={pageCount}
            onPageChange={handlePageClick}
            forcePage={pageMeta.page - 1}
          />
        </div>
      )}

      <CollectDeliveriesDataModal
        collectId={emissionDetailCollectId}
        onClose={() => {
          setEmissionDetailCollectId(undefined);
        }}
      />
    </div>
  );
};
